import React, { useRef, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  ScrollErrorForm,
  ConfirmationCheckbox,
  ConfirmModal,
  Notification,
  Spinner,
  PromoCode,
} from 'components'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { displayAttribute, formatCurrency } from 'utils'
import { isEmpty } from 'lodash'
import PaymentFields, { paymentSchema } from 'components/PaymentFields'
import { Variant as NotificationVariant } from 'components/Notification'
import { FlowActions } from '../components'
import { PAYMENT_CONFIRMATION_TEXT } from 'config/notification-services'
import { isPromoCodeEnabled } from 'config/portal'

const propTypes = {
  initialValues: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setIsNewPayment: PropTypes.func.isRequired,
  showReplacePayment: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  paymentMethod: PropTypes.array,
  onConfirmDeletePayment: PropTypes.func.isRequired,
  totalPaymentDue: PropTypes.number,
  hasCardOnFile: PropTypes.bool.isRequired,
  flashSuccessMessageHandler: PropTypes.func.isRequired,
  applyPromoCode: PropTypes.func.isRequired,
  totalParticipants: PropTypes.number.isRequired,
}

const defaultProps = {
  totalPaymentDue: null,
}

function ReviewAndSubmitForm({
  onSubmit,
  isLoading,
  initialValues,
  setIsNewPayment,
  showReplacePayment,
  paymentMethod,
  onConfirmDeletePayment,
  totalPaymentDue,
  hasCardOnFile,
  flashSuccessMessageHandler,
  applyPromoCode,
  totalParticipants,
}) {
  const [showRemovePaymentModal, setShowRemovePaymentModal] = useState(false)
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false)
  const hasPaymentMethod = !isEmpty(paymentMethod)
  const baseTotalRef = useRef(null)
  const confirmSubmissionSchema = useMemo(
    () =>
      Yup.object({
        paymentDetails: Yup.lazy((values, options) => {
          const discountedTotal =
            options.context.discountedTotal ?? totalPaymentDue ?? 0
          if (discountedTotal <= 0) return Yup.object().nullable()
          if (!hasPaymentMethod) {
            return paymentSchema
          }
          if (hasPaymentMethod && !!paymentMethod.creditCardLastFour) {
            return Yup.object().nullable()
          }
          if (hasPaymentMethod) {
            return paymentSchema
          }
          return Yup.object().nullable()
        }),
        confirmPayment: Yup.lazy((values, options) => {
          const discountedTotal =
            options.context.discountedTotal ?? totalPaymentDue ?? 0
          if (discountedTotal <= 0) return Yup.boolean().nullable()
          return Yup.boolean().required('Required').oneOf([true], 'Required')
        }),
      }),
    [hasPaymentMethod, paymentMethod, totalPaymentDue]
  )

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={confirmSubmissionSchema}
    >
      {({
        touched,
        errors,
        isSubmitting,
        values,
        setFieldValue,
        setFieldError,
      }) => {
        if (!values.promoApplied && !baseTotalRef.current) {
          baseTotalRef.current = totalPaymentDue
        }

        const baseTotal = baseTotalRef.current ?? totalPaymentDue
        const discountedTotal = values.discountedTotal ?? totalPaymentDue
        const discountAmount =
          baseTotal !== null && discountedTotal !== null
            ? baseTotal - discountedTotal
            : 0
        const isCalculationReady =
          totalPaymentDue !== null &&
          (!values.promoApplied || values.discountedTotal !== null)

        return (
          <ScrollErrorForm>
            <div className="form-section">
              {isLoading && (
                <div className="complete-services-agreement-view">
                  <Spinner />
                </div>
              )}
              {isPromoCodeEnabled() && (
                <PromoCode
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  totalDiscount={discountAmount}
                  flashSuccessMessageHandler={flashSuccessMessageHandler}
                  applyPromoCode={applyPromoCode}
                  orderParams={{ participantsCount: totalParticipants }}
                  normalizeResponse={(response) => ({
                    isPromoCodeValid: response.summary.isPromoCodeValid,
                    totalCost: response.summary.totalCost,
                  })}
                />
              )}
              <div className="payment-details-section">
                <h2>Total payment due: {formatCurrency(discountedTotal)}</h2>
                {((!showReplacePayment &&
                  !hasPaymentMethod &&
                  discountedTotal > 0) ||
                  (!hasCardOnFile && discountedTotal > 0)) && (
                  <div className="payment-replace-card">
                    <p>You do not have a card on file with Inspira.</p>
                    <button
                      className="button-text"
                      onClick={(e) => {
                        e.preventDefault()
                        setShowAddPaymentModal(true)
                      }}
                    >
                      Add new card
                    </button>
                  </div>
                )}
                {!showReplacePayment &&
                  hasPaymentMethod &&
                  hasCardOnFile &&
                  discountedTotal > 0 && (
                    <>
                      <div className="payment-replace-card">
                        <p>We'll charge your card on file with Inspira.</p>
                        <button
                          className="button-text"
                          onClick={(e) => {
                            e.preventDefault()
                            setShowRemovePaymentModal(true)
                          }}
                        >
                          Replace card
                        </button>
                      </div>
                      <div className="payment-details">
                        <div className="payment-details-header">
                          Credit card
                        </div>
                        <div className="payment-details-text">
                          {paymentMethod.cardType}:{' '}
                          {paymentMethod.creditCardLastFour
                            ? paymentMethod.creditCardLastFour
                            : paymentMethod.cardLabel}
                        </div>
                        <div className="payment-details-header">
                          Billing address
                        </div>
                        <div className="payment-details-text">
                          {displayAttribute(paymentMethod.address1)},{' '}
                          {displayAttribute(paymentMethod.city)},{' '}
                          {displayAttribute(paymentMethod.state)}{' '}
                          {displayAttribute(paymentMethod.zipCode)}
                        </div>
                      </div>
                    </>
                  )}
              </div>
              {touched.paymentDetails &&
                errors.paymentDetails &&
                discountedTotal > 0 && (
                  <Notification type={NotificationVariant.ERROR}>
                    {errors.paymentDetails.cardData}
                  </Notification>
                )}
              {showRemovePaymentModal && (
                <ConfirmModal
                  onClose={() => setShowRemovePaymentModal(false)}
                  onConfirm={() => {
                    setShowRemovePaymentModal(false)
                    setIsNewPayment(true)
                    onConfirmDeletePayment()
                  }}
                  confirmContent="Yes, Replace"
                  className="remove-payment-modal"
                >
                  <h2>Replace payment details</h2>
                  <p>
                    Keep in mind you can only have one card on file. Replacing
                    it here will replace it across the platform.
                  </p>
                </ConfirmModal>
              )}
              {showAddPaymentModal && (
                <ConfirmModal
                  onClose={() => setShowAddPaymentModal(false)}
                  onConfirm={() => {
                    setShowAddPaymentModal(false)
                    setIsNewPayment(true)
                    onConfirmDeletePayment()
                  }}
                  confirmContent="Yes"
                  className="remove-payment-modal"
                >
                  <h2>Add payment details</h2>
                </ConfirmModal>
              )}
              {showReplacePayment && discountedTotal > 0 && (
                <section className="new-payment-form">
                  <div id="add-new-payment-form-bd">
                    <PaymentFields sectionName="paymentDetails" />
                  </div>
                </section>
              )}
              {discountedTotal > 0 && (
                <ConfirmationCheckbox
                  name="confirmPayment"
                  label={PAYMENT_CONFIRMATION_TEXT}
                  errorText="You must agree to the disclosure to continue."
                />
              )}
            </div>
            <FlowActions
              isSubmitting={isSubmitting}
              isSubmitDisabled={!isCalculationReady}
              submitContent="Submit"
            />
          </ScrollErrorForm>
        )
      }}
    </Formik>
  )
}

ReviewAndSubmitForm.propTypes = propTypes
ReviewAndSubmitForm.defaultProps = defaultProps

export default ReviewAndSubmitForm
